// The one chart card frame (card 04 of mockup round 086,
// ui-map-interface.md §17.3.06 "The chart card frame").
//
// Every DOM card of the chart — the cartouche plate and the legend card —
// wears one frame: one width (the
// peek width), one pad, one head row and one minimize control in the
// top-right corner of the head row. The stylesheet draws the two readings
// of the frame from one class: the modern frame on the peek ground, and
// the historical frame with the inset double rule.
//
// **A word names a chart text step and never a size.** Each word role of a
// card names one step in the card's `text` table of `chart.toml` (note,
// label, name or feature, R1796), and the element carries the class
// `cs-<step>`. The stylesheet sets the step in the chrome ramp in the
// modern reading and in the Fell steps in the historical reading.

import { CHART_STEPS, type ChartStep } from '../../gen/presentation';

/** The class of a word of one chart text step. */
export function stepClass(step: ChartStep): string {
  if (!CHART_STEPS.includes(step)) throw new Error(`chart card: ${step} is not a chart text step`);
  return `cs-${step}`;
}

/** The minimize control of a card: its act and its accessible name. */
export interface CardMin {
  act: string;
  title: string;
  /** The card is folded or minimized now: the control then opens it. */
  folded: boolean;
}

/** The line icon of the minimize control: a minus, or a chevron up while
 *  the card is folded. The legend's chip draws the chevron too. */
export function minIcon(folded: boolean): string {
  const path = folded ? '<path d="M6 15l6-6 6 6"/>' : '<path d="M6 12h12"/>';
  return '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"'
    + ` stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
}

/**
 * The head row of a card: the title at the `name` step and, on a card that
 * minimizes, the control. The control is 24 px painted and 44 px under a
 * finger (the stylesheet), and it acts through the overlay's one delegate
 * on the furniture host, which reads `data-act`.
 */
export function cardHead(title: string, step: ChartStep, min?: CardMin): HTMLElement {
  const head = document.createElement('div');
  head.className = 'cc-head';
  const t = document.createElement('span');
  t.className = `cc-title ${stepClass(step)}`;
  t.textContent = title;
  head.appendChild(t);
  if (min) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'cc-min';
    b.dataset.act = min.act;
    b.title = min.title;
    b.setAttribute('aria-label', min.title);
    b.setAttribute('aria-expanded', String(!min.folded));
    b.innerHTML = minIcon(min.folded);
    head.appendChild(b);
  }
  return head;
}
